'use client';

import { motion } from 'framer-motion';
import { whitepaperGradient } from '@/lib/whitepaper-gradient';

// Spread opens with ease-out (fast → settle) so the fan feels snappy.
// Rest uses a longer ease-in-out so the sheets glide back together
// instead of snapping. Same stagger timing in both directions.
const EASE_OUT: [number, number, number, number]    = [0, 0, 0.2, 1];
const EASE_IN_OUT: [number, number, number, number] = [0.4, 0, 0.2, 1];
const T_SPREAD = { duration: 0.45, ease: EASE_OUT };
const T_REST   = { duration: 0.6,  ease: EASE_IN_OUT };

const wpThumb = {
  rest:   { transition: { staggerChildren: 0.06, staggerDirection: -1 as const } },
  spread: { transition: { staggerChildren: 0.06 } },
};
const wpPage1 = {
  rest:   { rotate: -4,   x: '-8%',  transition: T_REST },
  spread: { rotate: -9,   x: '-20%', transition: T_SPREAD },
};
const wpPage2 = {
  rest:   { rotate: -2,   x: '-4%',  transition: T_REST },
  spread: { rotate: -4.5, x: '-9%',  transition: T_SPREAD },
};
const wpPage3 = {
  rest:   { rotate: 0,    x:  '0%',  transition: T_REST },
  spread: { rotate: 1.5,  x:  '4%',  transition: T_SPREAD },
};

const FALLBACK_PAGES = [
  'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%203.png',
  'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%202.png',
  'https://storage.googleapis.com/cms_rewire/White-page-Letter%20-%201.png',
];


const PAGE_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: '-18%',
  left: '50%',
  marginLeft: '-22%',
  width: '44%',
  // Force every sheet to the same US-letter aspect ratio. Without this,
  // each PDF's natural page proportions drive the img height, so the
  // sheet tops sit at different positions across cards.
  aspectRatio: '8.5 / 11',
  objectFit: 'cover',
  objectPosition: 'top',
  transformOrigin: 'center bottom',
  boxShadow: 'var(--seasons-shadow-card)',
  borderRadius: 'var(--seasons-radius-sm)',
};

interface Props {
  pdfUrl?: string;
  /** Shown as full-cover background when no pdfUrl — keeps each card unique. */
  heroImg?: string;
}

/**
 * Stacked PDF page thumbnail with hover-fan animation.
 *
 * - All 3 pages load immediately (browser lazy-loads via scroll) so each card
 *   shows its own unique content at rest without waiting for hover.
 * - When no pdfUrl but heroImg is provided, renders the hero image as a plain
 *   cover (no page stack) so the card still looks unique.
 */
export function PdfThumbnail({ pdfUrl, heroImg }: Props) {
  function pageUrl(page: number): string {
    if (!pdfUrl) return FALLBACK_PAGES[3 - page];
    return `/api/pdf-pages?url=${encodeURIComponent(pdfUrl)}&page=${page}`;
  }

  // Per-card gradient — seeded from pdfUrl (preferred) or heroImg so the same
  // card always renders the same gradient but the grid feels varied.
  const gradient = whitepaperGradient(pdfUrl || heroImg);

  // No PDF — render heroImg as a clean cover over the gradient, or whitepaper
  // gradient bg with white pages stack
  if (!pdfUrl) {
    if (heroImg) {
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%', background: 'var(--seasons-color-surface-default)', overflow: 'hidden' }}>
          <img
            src={heroImg}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </div>
      );
    }
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', background: gradient, overflow: 'hidden' }} />
    );
  }

  // Has PDF — fan animation with all 3 unique pages loaded via scroll-lazy
  const backSrc  = pageUrl(3);
  const midSrc   = pageUrl(2);
  const frontSrc = pageUrl(1);

  return (
    <motion.div
      initial="rest"
      whileHover="spread"
      variants={wpThumb}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: gradient,
        overflow: 'hidden',
      }}
    >

      <motion.img
        key="back"
        src={backSrc}
        alt="" aria-hidden="true"
        loading="lazy" decoding="async"
        variants={wpPage1}
        style={{ ...PAGE_STYLE, zIndex: 1 }}
      />

      <motion.img
        key="mid"
        src={midSrc}
        alt="" aria-hidden="true"
        loading="lazy" decoding="async"
        variants={wpPage2}
        style={{ ...PAGE_STYLE, zIndex: 2 }}
      />

      <motion.img
        key="front"
        src={frontSrc}
        alt="" aria-hidden="true"
        loading="lazy" decoding="async"
        variants={wpPage3}
        style={{ ...PAGE_STYLE, zIndex: 3 }}
      />
    </motion.div>
  );
}
